export const CORE_ONBOARDING_STEP_DEFINITIONS = [
  { id: "language", path: "language" },
  { id: "theme", path: "theme" },
  { id: "room-categories", path: "room-categories" },
  { id: "rooms-counts", path: "rooms-counts" },
  { id: "rooms-grid", path: "rooms-grid" },
  { id: "room-rates", path: "room-rates" },
  { id: "reservation-settings", path: "reservation-settings" },
  { id: "extras-discounts", path: "extras-discounts" },
];

export function getOnboardingStepDefinitions({ bookingEngineEnabled = false } = {}) {
  return bookingEngineEnabled
    ? [...CORE_ONBOARDING_STEP_DEFINITIONS, { id: "booking-engine", path: "booking-engine" }]
    : CORE_ONBOARDING_STEP_DEFINITIONS.slice();
}

const ONBOARDING_MESSAGES = {
  en: {
    panel: {
      eyebrow: "Guided setup",
      title: "Welcome to Pilcore onboarding",
      copy:
        "We will set up your hotel one simple step at a time. You can move back whenever you need and keep your progress saved.",
      progressTitle: "Progress",
      progressDoneLabel: "Done",
      progressCompleted: (completed, total) => `${completed} of ${total} steps completed`,
      progressCompletedPercent: (value) => `${value}% completed`,
      progressPendingPercent: (value) => `${value}% pending`,
      stepsTitle: "Setup steps",
      currentBadge: "Current",
      doneBadge: "Done",
      readyBadge: "Ready",
      lockedBadge: "Locked",
      previousAction: "Previous",
      nextAction: "Next",
      saveAction: "Save",
    },
    progress: {
      stepCounter: (current, total) => `Step ${current} of ${total}`,
    },
    steps: {
      language: {
        title: "Language",
        description: "Choose how you want to use Pilcore during onboarding.",
      },
      theme: {
        title: "Theme",
        description: "Pick the look that will feel most comfortable while you work.",
      },
      "room-categories": {
        title: "Room categories",
        description: "Tell us the main room types your hotel sells.",
      },
      rooms: {
        title: "Rooms",
        description: "Create the rooms in each category without repeating the same data.",
      },
    },
    languageStep: {
      eyebrow: "First things first",
      title: "Choose your language",
      copy:
        "This choice updates both onboarding and Pilcore, so everything feels familiar from the very beginning.",
      helper: "You can change it later from Pilcore settings whenever you want.",
      continue: "Continue",
      selectedTag: "Selected",
    },
    themeStep: {
      eyebrow: "Make it comfortable",
      title: "Choose your theme",
      copy:
        "Pick the look you want to use while working in Pilcore. We recommend Dark, the original Pilcore style, because it is the one most teams already know.",
      helper: "You can change it later from Pilcore settings whenever you want.",
      continue: "Continue",
      selectedTag: "Selected",
      recommendedTag: "Recommended",
      originalTag: "Original",
      dark: {
        title: "Dark",
        description: "The original Pilcore look and our recommended choice for onboarding.",
      },
      light: {
        title: "Light",
        description: "A brighter workspace if your team prefers a lighter interface.",
      },
    },
    setupGate: {
      eyebrow: "Before you start",
      loadingTitle: "Preparing your onboarding",
      loadingCopy: "We are checking the admin setup for this hotel before opening the onboarding forms.",
      title: "This onboarding is not ready yet",
      copy:
        "An admin still needs to prepare this hotel before the onboarding forms can be opened. This does not affect your live Pilcore access.",
      statusLabel: "Current setup status",
      bookingEngineLabel: "Booking Engine",
      readyStatus: "Ready",
      draftStatus: "Waiting for admin preparation",
      missingStatus: "Not prepared yet",
      bookingEnginePilcore: "Pilcore Booking Engine included",
      bookingEngineNone: "No Booking Engine included",
      retryAction: "Check again",
      backAction: "Back to modules",
    },
    roomCategories: {
      eyebrow: "Hotel setup",
      title: "Tell us about your room types",
      copy:
        "Start with the room categories you already sell. Keep it simple: the main categories are enough for a first setup.",
      add: "Add room type",
      emptyTitle: "Start with your first room type",
      emptyCopy: "Examples: Standard Double, Junior Suite or Family Room.",
      itemTitle: (index) => `Room type ${index}`,
      itemCopy: "Use the commercial name guests and your team already know.",
      name: "Name",
      namePlaceholder: "Deluxe Sea View",
      maxAdults: "Max adults",
      maxAdultsHelpAria: "What does max adults mean?",
      maxAdultsHelp:
        "Set the highest number of adults you want to allow in this room type.\n\nThis number must respect both the physical capacity of the room and your property's accommodation policies.\n\nThe minimum is 1, because at least one adult must stay in the room.\n\nExample: if 2 adults could fit physically, but your hotel only wants to allow 1 adult in this room type, you can set 1.",
      maxChildren: "Max children",
      maxChildrenHelpAria: "What does max children mean?",
      maxChildrenHelp:
        "Set the highest number of children you want to allow in this room type.\n\nThis can be 0 if children are not accepted.\n\nA child can use an adult space, and beds intended only for children also count.\n\nExample: if the room has 2 adult spaces and 1 child bed, up to 2 children could fit physically. If that matches your accommodation policies, you can set 2. If your hotel only wants to allow 1 child, set 1.",
      maxOccupancy: "Max occupancy",
      maxOccupancyHelpAria: "What does max occupancy mean?",
      maxOccupancyHelp:
        "This is the highest total number of guests you want to allow in the room, counting adults and children together.\n\nUse the combined limit that matches both the physical capacity and your accommodation policies.\n\nExample 1: if the room has 2 adult spaces and 1 child bed, the physical maximum is 3. You can set max occupancy to 3 if your hotel wants to allow that combination.\n\nExample 2: if the room has 1 adult bed and 2 child beds, the physical maximum is 3, but you can still choose a lower limit if your policies are more restrictive.",
      maxOccupancyPlaceholder: "Optional",
      description: "Short description",
      descriptionPlaceholder: "Optional note to help us understand this room type",
      remove: "Remove",
      save: "Save progress",
      reload: "Reload",
      saveSuccess: "Your progress has been saved.",
      loadError: "We could not load your saved progress. You can still continue from here.",
      saveError: "We could not save your progress right now.",
      validationName: (index) => `Room type ${index} needs a name or should be removed.`,
      validationAdults: (index) => `Room type ${index} needs at least 1 adult.`,
      validationChildren: (index) => `Room type ${index} cannot have a negative children value.`,
      validationOccupancy: (index) => `Max occupancy in room type ${index} cannot be lower than max adults.`,
      summaryTitle: "A few tips",
      summaryIntro: "Keep this step easy and practical.",
      summaryPoints: [
        "Start with the room types you sell most often.",
        "Use names your guests will recognize.",
        "You can keep descriptions short for now.",
      ],
      summaryCountLabel: "Room types in this step",
      summaryLanguageLabel: "Current language",
      summarySavedLabel: "Last save",
      summaryNotSaved: "Not saved yet",
    },
  },
  es: {
    panel: {
      eyebrow: "Configuración guiada",
      title: "Bienvenido al onboarding de Pilcore",
      copy:
        "Vamos a configurar tu hotel paso a paso de forma sencilla. Puedes volver atrás cuando quieras y mantener tu progreso guardado.",
      progressTitle: "Progreso",
      progressDoneLabel: "Hecho",
      progressCompleted: (completed, total) => `${completed} de ${total} pasos completados`,
      progressCompletedPercent: (value) => `${value}% completado`,
      progressPendingPercent: (value) => `${value}% pendiente`,
      stepsTitle: "Pasos de configuración",
      currentBadge: "Actual",
      doneBadge: "Hecho",
      readyBadge: "Listo",
      lockedBadge: "Bloqueado",
      previousAction: "Anterior",
      nextAction: "Siguiente",
      saveAction: "Guardar",
    },
    progress: {
      stepCounter: (current, total) => `Paso ${current} de ${total}`,
    },
    steps: {
      language: {
        title: "Idioma",
        description: "Elige cómo quieres usar Pilcore durante el onboarding.",
      },
      theme: {
        title: "Tema",
        description: "Escoge el aspecto con el que te resultará más cómodo trabajar.",
      },
      "room-categories": {
        title: "Categorías de habitaciones",
        description: "Cuéntanos los tipos principales de habitaciones que vende tu hotel.",
      },
    },
    languageStep: {
      eyebrow: "Empezamos por algo fácil",
      title: "Elige tu idioma",
      copy:
        "Esta elección actualiza tanto el onboarding como Pilcore, para que todo te resulte familiar desde el principio.",
      helper: "Podrás cambiarlo más adelante desde la configuración de Pilcore cuando quieras.",
      continue: "Continuar",
      selectedTag: "Seleccionado",
    },
    themeStep: {
      eyebrow: "Déjalo a tu gusto",
      title: "Elige tu tema",
      copy:
        "Escoge el aspecto con el que quieres usar Pilcore. Recomendamos el oscuro, que es el estilo original de Pilcore y el que más equipos ya conocen.",
      helper: "Podrás cambiarlo más adelante desde la configuración de Pilcore cuando quieras.",
      continue: "Continuar",
      selectedTag: "Seleccionado",
      recommendedTag: "Recomendado",
      originalTag: "Original",
      dark: {
        title: "Oscuro",
        description: "El aspecto original de Pilcore y nuestra opción recomendada para el onboarding.",
      },
      light: {
        title: "Claro",
        description: "Una interfaz más luminosa si tu equipo prefiere trabajar con tonos claros.",
      },
    },
    setupGate: {
      eyebrow: "Antes de empezar",
      loadingTitle: "Preparando tu onboarding",
      loadingCopy: "Estamos comprobando la configuracion que ha dejado preparada el admin para este hotel antes de abrir los formularios.",
      title: "Este onboarding todavia no esta listo",
      copy:
        "Un admin todavia tiene que preparar este hotel antes de que se puedan abrir los formularios de onboarding. Esto no afecta a tu acceso live de Pilcore.",
      statusLabel: "Estado actual",
      bookingEngineLabel: "Booking Engine",
      readyStatus: "Listo",
      draftStatus: "Pendiente de preparacion por admin",
      missingStatus: "Todavia sin preparar",
      bookingEnginePilcore: "Pilcore Booking Engine incluido",
      bookingEngineNone: "Sin Booking Engine",
      retryAction: "Comprobar de nuevo",
      backAction: "Volver a modulos",
    },
    roomCategories: {
      eyebrow: "Configuración del hotel",
      title: "Cuéntanos tus tipos de habitación",
      copy:
        "Empieza por las categorías que ya vendes hoy. Hazlo simple: con las principales es suficiente para una primera configuración.",
      add: "Añadir tipo de habitación",
      emptyTitle: "Empieza con tu primer tipo de habitación",
      emptyCopy: "Ejemplos: Doble Estándar, Junior Suite o Familiar.",
      itemTitle: (index) => `Tipo de habitación ${index}`,
      itemCopy: "Usa el nombre comercial que ya conoce tu equipo y también tus clientes.",
      name: "Nombre",
      namePlaceholder: "Deluxe Vista Mar",
      maxAdults: "Máx. adultos",
      maxAdultsHelpAria: "¿Qué significa Máx. adultos?",
      maxAdultsHelp:
        "Indica el número máximo de adultos que quieres permitir en este tipo de habitación.\n\nEste valor debe respetar tanto la capacidad física de la habitación como las políticas de alojamiento de tu establecimiento.\n\nEl mínimo es 1, porque en la habitación siempre debe alojarse al menos un adulto.\n\nEjemplo: si físicamente caben 2 adultos, pero tu alojamiento solo quiere permitir 1 adulto en esta tipología, aquí puedes poner 1.",
      maxChildren: "Máx. niños",
      maxChildrenHelpAria: "¿Qué significa Máx. niños?",
      maxChildrenHelp:
        "Indica el número máximo de niños que quieres permitir en este tipo de habitación.\n\nPuede ser 0 si en esta habitación no se aceptan niños.\n\nUn niño puede ocupar una plaza de adulto, y también cuenta cualquier cama pensada solo para niños.\n\nEjemplo: si la habitación tiene 2 plazas de adulto y 1 cama de niño, físicamente podrían alojarse hasta 2 niños. Si eso encaja con las políticas de tu alojamiento, puedes poner 2. Si tu establecimiento solo quiere admitir 1 niño, entonces debes poner 1.",
      maxOccupancy: "Ocupación máxima",
      maxOccupancyHelpAria: "¿Qué significa Ocupación máxima?",
      maxOccupancyHelp:
        "Es el número máximo total de personas que quieres permitir en la habitación, sumando adultos y niños.\n\nMarca aquí el límite combinado, siempre dentro de lo que cabe físicamente y de lo que permitan las políticas de tu alojamiento.\n\nEjemplo 1: si la habitación tiene 2 plazas de adulto y 1 cama de niño, físicamente pueden alojarse 3 personas. Puedes poner una ocupación máxima de 3 si tu alojamiento quiere permitir esa combinación.\n\nEjemplo 2: si la habitación tiene 1 cama de adulto y 2 camas de niño, físicamente caben 3 personas, pero puedes fijar una ocupación máxima más baja si tus políticas son más restrictivas.",
      maxOccupancyPlaceholder: "Opcional",
      description: "Descripción breve",
      descriptionPlaceholder: "Nota opcional para ayudarnos a entender esta categoría",
      remove: "Eliminar",
      save: "Guardar progreso",
      reload: "Recargar",
      saveSuccess: "Tu progreso se ha guardado correctamente.",
      loadError: "No hemos podido cargar tu progreso guardado. Puedes seguir desde aquí.",
      saveError: "No hemos podido guardar tu progreso ahora mismo.",
      validationName: (index) => `El tipo de habitación ${index} necesita un nombre o debes eliminarlo.`,
      validationAdults: (index) => `El tipo de habitación ${index} necesita al menos 1 adulto.`,
      validationChildren: (index) => `El tipo de habitación ${index} no puede tener niños negativos.`,
      validationOccupancy: (index) =>
        `La ocupación máxima del tipo de habitación ${index} no puede ser menor que el máximo de adultos.`,
      summaryTitle: "Unas pistas rápidas",
      summaryIntro: "Haz este paso fácil y práctico.",
      summaryPoints: [
        "Empieza por las categorías que más vendes.",
        "Usa nombres que el cliente reconozca.",
        "Las descripciones pueden ser cortas por ahora.",
      ],
      summaryCountLabel: "Tipos de habitación en este paso",
      summaryLanguageLabel: "Idioma actual",
      summarySavedLabel: "Último guardado",
      summaryNotSaved: "Todavía sin guardar",
    },
  },
  ca: {
    panel: {
      eyebrow: "Configuració guiada",
      title: "Benvingut a l'onboarding de Pilcore",
      copy:
        "Configurarem l'hotel pas a pas d'una manera senzilla. Pots tornar enrere quan vulguis i conservar el progrés guardat.",
      progressTitle: "Progrés",
      progressDoneLabel: "Fet",
      progressCompleted: (completed, total) => `${completed} de ${total} passos completats`,
      progressCompletedPercent: (value) => `${value}% completat`,
      progressPendingPercent: (value) => `${value}% pendent`,
      stepsTitle: "Passos de configuració",
      currentBadge: "Actual",
      doneBadge: "Fet",
      readyBadge: "A punt",
      lockedBadge: "Bloquejat",
      previousAction: "Anterior",
      nextAction: "Seguent",
      saveAction: "Guardar",
    },
    progress: {
      stepCounter: (current, total) => `Pas ${current} de ${total}`,
    },
    steps: {
      language: {
        title: "Idioma",
        description: "Tria com vols utilitzar Pilcore durant l'onboarding.",
      },
      theme: {
        title: "Tema",
        description: "Escull l'aspecte amb què et serà més còmode treballar.",
      },
      "room-categories": {
        title: "Categories d'habitacions",
        description: "Explica'ns els tipus principals d'habitacions que ven el teu hotel.",
      },
    },
    languageStep: {
      eyebrow: "Comencem per una cosa fàcil",
      title: "Tria el teu idioma",
      copy:
        "Aquesta elecció actualitza tant l'onboarding com Pilcore, perquè tot et resulti familiar des del primer moment.",
      helper: "Ho podràs canviar més endavant des de la configuració de Pilcore quan vulguis.",
      continue: "Continuar",
      selectedTag: "Seleccionat",
    },
    themeStep: {
      eyebrow: "Posa-ho al teu gust",
      title: "Tria el teu tema",
      copy:
        "Escull l'aspecte amb què vols utilitzar Pilcore. Recomanem el fosc, que és l'estil original de Pilcore i el que més equips ja coneixen.",
      helper: "Ho podràs canviar més endavant des de la configuració de Pilcore quan vulguis.",
      continue: "Continuar",
      selectedTag: "Seleccionat",
      recommendedTag: "Recomanat",
      originalTag: "Original",
      dark: {
        title: "Fosc",
        description: "L'aspecte original de Pilcore i la nostra opció recomanada per a l'onboarding.",
      },
      light: {
        title: "Clar",
        description: "Una interfície més lluminosa si el teu equip prefereix treballar amb tons clars.",
      },
    },
    setupGate: {
      eyebrow: "Abans de comencar",
      loadingTitle: "Preparant el teu onboarding",
      loadingCopy: "Estem comprovant la configuracio que ha deixat preparada l'admin per a aquest hotel abans d'obrir els formularis.",
      title: "Aquest onboarding encara no esta a punt",
      copy:
        "Un admin encara ha de preparar aquest hotel abans que es puguin obrir els formularis d'onboarding. Aixo no afecta el teu acces live a Pilcore.",
      statusLabel: "Estat actual",
      bookingEngineLabel: "Booking Engine",
      readyStatus: "A punt",
      draftStatus: "Pendent de preparacio per admin",
      missingStatus: "Encara sense preparar",
      bookingEnginePilcore: "Pilcore Booking Engine inclos",
      bookingEngineNone: "Sense Booking Engine",
      retryAction: "Comprovar de nou",
      backAction: "Tornar als moduls",
    },
    roomCategories: {
      eyebrow: "Configuració de l'hotel",
      title: "Explica'ns els teus tipus d'habitació",
      copy:
        "Comença per les categories que ja vens avui. Fes-ho simple: amb les principals n'hi ha prou per a una primera configuració.",
      add: "Afegir tipus d'habitació",
      emptyTitle: "Comença amb el teu primer tipus d'habitació",
      emptyCopy: "Exemples: Doble Estàndard, Junior Suite o Familiar.",
      itemTitle: (index) => `Tipus d'habitació ${index}`,
      itemCopy: "Fes servir el nom comercial que ja coneixen el teu equip i els teus clients.",
      name: "Nom",
      namePlaceholder: "Deluxe Vista Mar",
      maxAdults: "Màx. adults",
      maxAdultsHelpAria: "Què significa Màx. adults?",
      maxAdultsHelp:
        "Indica el nombre màxim d'adults que vols permetre en aquest tipus d'habitació.\n\nAquest valor ha de respectar tant la capacitat física de l'habitació com les polítiques d'allotjament del teu establiment.\n\nEl mínim és 1, perquè a l'habitació sempre hi ha d'haver almenys un adult.\n\nExemple: si físicament hi caben 2 adults, però el teu allotjament només en vol permetre 1 en aquesta tipologia, aquí pots posar 1.",
      maxChildren: "Màx. nens",
      maxChildrenHelpAria: "Què significa Màx. nens?",
      maxChildrenHelp:
        "Indica el nombre màxim de nens que vols permetre en aquest tipus d'habitació.\n\nPot ser 0 si en aquesta habitació no s'accepten nens.\n\nUn nen pot ocupar una plaça d'adult, i també compta qualsevol llit pensat només per a nens.\n\nExemple: si l'habitació té 2 places d'adult i 1 llit de nen, físicament s'hi poden allotjar fins a 2 nens. Si això encaixa amb les polítiques del teu allotjament, pots posar 2. Si el teu establiment només vol admetre 1 nen, aleshores has de posar 1.",
      maxOccupancy: "Ocupació màxima",
      maxOccupancyHelpAria: "Què significa Ocupació màxima?",
      maxOccupancyHelp:
        "És el nombre màxim total de persones que vols permetre a l'habitació, sumant adults i nens.\n\nMarca aquí el límit combinat, sempre dins del que hi cap físicament i del que permetin les polítiques del teu allotjament.\n\nExemple 1: si l'habitació té 2 places d'adult i 1 llit de nen, físicament s'hi poden allotjar 3 persones. Pots posar una ocupació màxima de 3 si el teu allotjament vol permetre aquesta combinació.\n\nExemple 2: si l'habitació té 1 llit d'adult i 2 llits de nen, físicament hi caben 3 persones, però pots fixar una ocupació màxima més baixa si les teves polítiques són més restrictives.",
      maxOccupancyPlaceholder: "Opcional",
      description: "Descripció breu",
      descriptionPlaceholder: "Nota opcional per ajudar-nos a entendre aquesta categoria",
      remove: "Eliminar",
      save: "Guardar progrés",
      reload: "Recarregar",
      saveSuccess: "El teu progrés s'ha guardat correctament.",
      loadError: "No hem pogut carregar el teu progrés guardat. Pots continuar des d'aquí.",
      saveError: "No hem pogut guardar el teu progrés ara mateix.",
      validationName: (index) => `El tipus d'habitació ${index} necessita un nom o l'has d'eliminar.`,
      validationAdults: (index) => `El tipus d'habitació ${index} necessita almenys 1 adult.`,
      validationChildren: (index) => `El tipus d'habitació ${index} no pot tenir nens negatius.`,
      validationOccupancy: (index) =>
        `L'ocupació màxima del tipus d'habitació ${index} no pot ser inferior al màxim d'adults.`,
      summaryTitle: "Algunes pistes ràpides",
      summaryIntro: "Fes aquest pas fàcil i pràctic.",
      summaryPoints: [
        "Comença per les categories que més vens.",
        "Fes servir noms que el client reconegui.",
        "Les descripcions poden ser curtes de moment.",
      ],
      summaryCountLabel: "Tipus d'habitació en aquest pas",
      summaryLanguageLabel: "Idioma actual",
      summarySavedLabel: "Últim guardat",
      summaryNotSaved: "Encara sense guardar",
    },
  },
};

const ONBOARDING_ADDITIONAL_STEPS = {
  en: {
    "rooms-counts": {
      title: "Room counts",
      description: "Set how many rooms there are in each category.",
    },
    "rooms-grid": {
      title: "Room details",
      description: "Complete the room details in a simple editable grid.",
    },
    "room-rates": {
      title: "Room rates",
      description: "Create the first room rates and basic stay rules.",
    },
    "reservation-settings": {
      title: "Reservation settings",
      description: "Set the reservation basics, locked defaults and starter email setup.",
    },
    "extras-discounts": {
      title: "Extras & discounts",
      description: "Optionally prepare extras and discounts before going live.",
    },
    "booking-engine": {
      title: "Booking Engine",
      description: "Stage the direct-booking experience, commercial copy and room imagery.",
    },
  },
  es: {
    "rooms-counts": {
      title: "Cantidad de habitaciones",
      description: "Indica cuántas habitaciones hay en cada categoría.",
    },
    "rooms-grid": {
      title: "Detalle de habitaciones",
      description: "Completa los datos de las habitaciones en una parrilla editable.",
    },
    "room-rates": {
      title: "Tarifas",
      description: "Crea las primeras tarifas y sus reglas basicas de estancia.",
    },
    "reservation-settings": {
      title: "Ajustes de reserva",
      description: "Define la base operativa de reservas, los defaults bloqueados y el email inicial.",
    },
    "extras-discounts": {
      title: "Extras y descuentos",
      description: "Prepara extras y descuentos de forma opcional antes de salir a produccion.",
    },
    "booking-engine": {
      title: "Booking Engine",
      description: "Deja listo en staging el canal directo, el copy comercial y las imagenes de venta.",
    },
  },
  ca: {
    "rooms-counts": {
      title: "Quantitat d'habitacions",
      description: "Indica quantes habitacions hi ha a cada categoria.",
    },
    "rooms-grid": {
      title: "Detall d'habitacions",
      description: "Completa les dades de les habitacions en una graella editable.",
    },
    "room-rates": {
      title: "Room rates",
      description: "Crea les primeres tarifes i les seves regles basiques d'estada.",
    },
    "reservation-settings": {
      title: "Configuracio de reserves",
      description: "Defineix la base operativa de reserves, els defaults bloquejats i l'email inicial.",
    },
    "extras-discounts": {
      title: "Extres i descomptes",
      description: "Prepara extres i descomptes de manera opcional abans de sortir a produccio.",
    },
    "booking-engine": {
      title: "Booking Engine",
      description: "Deixa preparat en staging el canal directe, el copy comercial i les imatges de venda.",
    },
  },
};

export function getOnboardingMessages(languageId) {
  const resolvedLanguageId = ONBOARDING_MESSAGES[languageId] ? languageId : "en";
  const baseMessages = ONBOARDING_MESSAGES[resolvedLanguageId] || ONBOARDING_MESSAGES.en;
  const extraSteps = ONBOARDING_ADDITIONAL_STEPS[resolvedLanguageId] || {};

  return {
    ...baseMessages,
    steps: {
      ...baseMessages.steps,
      ...extraSteps,
    },
  };
}
